import { useCurrentChapter } from '@audacy-clients/core/atoms/chapters';
import { ViewIDKey } from '@audacy-clients/core/atoms/config/constants';

import { useFeatureFlag, useViewId } from '@audacy-clients/core/atoms/config/settings';
import { RequestType } from '@audacy-clients/core/atoms/modules/requests';
import { nowPlaying } from '@audacy-clients/core/atoms/player';
import { isTranscriptEntityValid } from '@audacy-clients/core/hooks/useTranscript';
import { Suspense } from 'react';

import { useRecoilValue } from 'recoil';
import About from '~/components/About';
import ChaptersWithState from '~/components/Chapters';
import CenteredLoading from '~/components/Loading/CenteredLoading';
import ModuleRenderer from '~/components/ModuleRenderer';
import TranscriptPanel from '~/components/Player/FullscreenPlayer/Components/TranscriptPanel';
import PlaylistBar from '~/components/Player/PlaylistBar';
import { usePlaylistBarProps } from '~/components/Player/PlaylistBar/hooks';
import usePlayerProps from '~/components/Player/hooks/usePlayerProps';
import StandaloneChapterEpisodeCard from '~/components/StandaloneChapterEpisodeCard';
import { useBreakpoints } from '~/hooks/use-breakpoints';
import { Mixins } from '~/styles';

import styles from './styles';

type IInfoProps = {
  isVisible: boolean;
};

const InfoPanel = ({ isVisible }: IInfoProps): JSX.Element => {
  const isPodcastTranscriptEnabled = useFeatureFlag('podcastTranscript');
  const { episodeDataObject, standaloneChapterDataObject } = useRecoilValue(nowPlaying) ?? {};
  const entityDataObject = episodeDataObject ?? standaloneChapterDataObject;
  const currentChapter = useCurrentChapter({ live: true });

  const isEntityValidForTranscript = isTranscriptEntityValid(entityDataObject, currentChapter);

  const viewId = useViewId(ViewIDKey.Player);
  const { contentId, description, episode, standaloneChapter } = usePlayerProps();
  const {
    nextUpTitle,
    playlistTitle,
    playlistImage,
    playlistImageAlt,
    navigateToPlaylistUrl,
    showPlaylistBar,
  } = usePlaylistBarProps();

  const { greaterThan } = useBreakpoints();

  if (!contentId || !viewId || !isVisible) {
    return <CenteredLoading />;
  }

  return (
    <Suspense fallback={<CenteredLoading />}>
      <div css={styles.container}>
        {showPlaylistBar && (
          <PlaylistBar
            title={playlistTitle}
            nextUpTitle={nextUpTitle}
            navigateToPlaylistUrl={navigateToPlaylistUrl}
            image={playlistImage}
            imageAlt={playlistImageAlt}
            containerCss={Mixins.horizontalModulePadding(true)}
            showSectionWrapper={greaterThan.MD}
          />
        )}
        {episode && episode.isRewind !== false && !standaloneChapter && (
          <ChaptersWithState
            episode={episode}
            live={true}
            containerCss={[styles.chapters, Mixins.horizontalModulePadding(true)]}
          />
        )}

        {greaterThan.MD && isPodcastTranscriptEnabled && isEntityValidForTranscript && (
          <div css={styles.module}>
            <TranscriptPanel hasChapter={!!currentChapter} />
          </div>
        )}

        {standaloneChapter && (
          <div css={styles.module}>
            <StandaloneChapterEpisodeCard
              standaloneChapter={standaloneChapter}
              containerCss={Mixins.horizontalModulePadding(true)}
            />
          </div>
        )}
        {description && (
          <div css={styles.module}>
            <About
              text={description}
              textCss={styles.text}
              containerCss={Mixins.horizontalModulePadding(true)}
            />
          </div>
        )}
        {/* TODO: [CCS-2922]: Remove standaloneChapter from the
            condition when product decides to include modules for standalone
            chapters */}
        {!standaloneChapter && (
          <ModuleRenderer
            request={{
              contentId,
              type: RequestType.ContentPage,
              viewId,
            }}
          />
        )}
      </div>
    </Suspense>
  );
};

export default InfoPanel;
